import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import AppText from "../../design-system/components/AppText";
import { buildAxisTicks, minutesToLabel } from "./occupancyModel";

const MINI_WIDTH = 120;
const MINI_HEIGHT = 40;

const DETAIL_WIDTH = 920;
const DETAIL_HEIGHT = 240;
const DETAIL_PADDING = { top: 16, right: 16, bottom: 30, left: 30 };

// Kein Kollisions-Layout fuer die Breakpoint-Labels der grossen Ansicht — bei
// vielen Schichtwechseln an einem Tag wuerden sie sich sonst ueberlappen.
// Ein Label wird nur gezeichnet, wenn es mindestens so viel Pixelabstand zum
// zuletzt gezeichneten hat (vgl. dataviz-Skill: "when end-labels collide,
// don't stack them" — hier durch Weglassen statt Verschieben geloest, weil es
// Zeitpunkte auf einer Achse sind, kein konvergierendes Serienende).
const MIN_LABEL_GAP_PX = 34;

const buildStepPoints = (segments, xScale, yScale) => {
  const points = [];
  segments.forEach((segment) => {
    const x0 = xScale(segment.start);
    const x1 = xScale(segment.end);
    const y = yScale(segment.value);
    points.push([x0, y], [x1, y]);
  });
  return points;
};

const pointsToPath = (points) => (
  points.map(([x, y], index) => `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ")
);

const segmentAt = (segments, minutes) => (
  segments.find((segment) => segment.start <= minutes && minutes <= segment.end) || null
);

const OccupancyChart = ({ curve, variant = "mini" }) => {
  const { t } = useTranslation();
  const isDetail = variant === "detail";
  const width = isDetail ? DETAIL_WIDTH : MINI_WIDTH;
  const height = isDetail ? DETAIL_HEIGHT : MINI_HEIGHT;
  const padding = isDetail ? DETAIL_PADDING : { top: 3, right: 1, bottom: 3, left: 1 };
  const svgRef = useRef(null);
  const [hoverMinutes, setHoverMinutes] = useState(null);

  const { rangeStart, rangeEnd, segments, min, max } = curve;
  const span = Math.max(1, rangeEnd - rangeStart);
  const innerWidth = width - padding.left - padding.right;
  const innerHeight = height - padding.top - padding.bottom;

  const ticks = useMemo(
    () => (isDetail ? buildAxisTicks(max) : [0, max]),
    [isDetail, max]
  );
  const topValue = isDetail ? ticks[ticks.length - 1] : Math.max(max * 1.08, max + 1);

  const xScale = (minutes) => padding.left + ((minutes - rangeStart) / span) * innerWidth;
  const yScale = (value) => padding.top + (1 - (topValue ? value / topValue : 0)) * innerHeight;

  const stepPoints = useMemo(() => buildStepPoints(segments, xScale, yScale), [segments, width, height]);
  const linePath = pointsToPath(stepPoints);
  const areaPath = stepPoints.length
    ? `${linePath} L${stepPoints[stepPoints.length - 1][0].toFixed(2)},${yScale(0).toFixed(2)} `
      + `L${stepPoints[0][0].toFixed(2)},${yScale(0).toFixed(2)} Z`
    : "";

  const declutteredBreakpointLabels = useMemo(() => {
    if (!isDetail) return [];
    let lastX = -Infinity;
    return curve.breakpoints.filter((minutes) => {
      const x = xScale(minutes);
      if (x - lastX < MIN_LABEL_GAP_PX) return false;
      lastX = x;
      return true;
    });
  }, [isDetail, curve.breakpoints, width]);

  const moveHover = (minutes) => {
    const clamped = Math.min(rangeEnd, Math.max(rangeStart, minutes));
    setHoverMinutes(clamped);
  };

  const handlePointerMove = (event) => {
    if (!isDetail || !svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    moveHover(rangeStart + ratio * span);
  };

  const handleKeyDown = (event) => {
    if (!isDetail) return;
    const breakpoints = curve.breakpoints;
    if (!breakpoints.length) return;
    const current = hoverMinutes == null ? breakpoints[0] : hoverMinutes;
    const currentIndex = breakpoints.reduce((closest, value, index) => (
      Math.abs(value - current) < Math.abs(breakpoints[closest] - current) ? index : closest
    ), 0);

    if (event.key === "ArrowRight") {
      event.preventDefault();
      moveHover(breakpoints[Math.min(breakpoints.length - 1, currentIndex + 1)]);
    } else if (event.key === "ArrowLeft") {
      event.preventDefault();
      moveHover(breakpoints[Math.max(0, currentIndex - 1)]);
    } else if (event.key === "Home") {
      event.preventDefault();
      moveHover(breakpoints[0]);
    } else if (event.key === "End") {
      event.preventDefault();
      moveHover(breakpoints[breakpoints.length - 1]);
    }
  };

  const hoverSegment = hoverMinutes == null ? null : segmentAt(segments, hoverMinutes);
  const hoverX = hoverMinutes == null ? null : xScale(hoverMinutes);
  const hoverY = hoverSegment ? yScale(hoverSegment.value) : null;
  const tooltipOnLeftHalf = hoverX != null && hoverX > width / 2;

  return (
    <div className={`demand-v2__occupancy-chart demand-v2__occupancy-chart--${variant}`}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${width} ${height}`}
        className="demand-v2__occupancy-svg"
        preserveAspectRatio="none"
        role="img"
        aria-hidden={!isDetail}
        {...(isDetail ? {
          tabIndex: 0,
          onPointerMove: handlePointerMove,
          onPointerLeave: () => setHoverMinutes(null),
          onFocus: () => moveHover(curve.breakpoints[0]),
          onBlur: () => setHoverMinutes(null),
          onKeyDown: handleKeyDown,
        } : {})}
      >
        {isDetail ? ticks.map((tickValue) => (
          <g key={tickValue}>
            <line
              x1={padding.left}
              x2={width - padding.right}
              y1={yScale(tickValue)}
              y2={yScale(tickValue)}
              className="demand-v2__occupancy-gridline"
            />
            <text
              x={padding.left - 6}
              y={yScale(tickValue)}
              className="demand-v2__occupancy-axis-label"
              textAnchor="end"
              dominantBaseline="middle"
            >
              {tickValue}
            </text>
          </g>
        )) : null}

        {areaPath ? <path d={areaPath} className="demand-v2__occupancy-area" /> : null}
        {linePath ? <path d={linePath} className="demand-v2__occupancy-line" fill="none" /> : null}

        {isDetail ? declutteredBreakpointLabels.map((minutes) => (
          <text
            key={minutes}
            x={xScale(minutes)}
            y={height - padding.bottom + 16}
            className="demand-v2__occupancy-axis-label"
            textAnchor="middle"
          >
            {minutesToLabel(minutes)}
          </text>
        )) : null}

        {isDetail && hoverX != null ? (
          <g className="demand-v2__occupancy-hover" aria-hidden="true">
            <line
              x1={hoverX}
              x2={hoverX}
              y1={padding.top}
              y2={height - padding.bottom}
              className="demand-v2__occupancy-crosshair"
            />
            {hoverY != null ? (
              <circle cx={hoverX} cy={hoverY} r={4} className="demand-v2__occupancy-dot" />
            ) : null}
          </g>
        ) : null}
      </svg>

      {isDetail && hoverSegment ? (
        <div
          className="demand-v2__occupancy-tooltip"
          style={{
            left: `${(hoverX / width) * 100}%`,
            transform: tooltipOnLeftHalf ? "translate(-100%, -8px)" : "translate(8px, -8px)",
          }}
          role="status"
        >
          <AppText as="strong" variant="label-2">{hoverSegment.value}</AppText>
          <AppText variant="label-3" tone="tertiary">{minutesToLabel(hoverMinutes)}</AppText>
        </div>
      ) : null}

      {!isDetail ? (
        <div className="demand-v2__occupancy-minmax">
          <AppText variant="label-3" tone="tertiary">
            {t("demand_ui.occupancy_min")} <strong>{min}</strong>
          </AppText>
          <AppText variant="label-3" tone="tertiary">
            {t("demand_ui.occupancy_max")} <strong>{max}</strong>
          </AppText>
        </div>
      ) : null}
    </div>
  );
};

export default OccupancyChart;
